import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { PartsEditor } from "./PartsEditor";
import { InvoiceScanButton } from "./InvoiceScanButton";
import { WarehouseMatchPanel } from "./WarehouseMatchPanel";

const FIELDS = [["plate_number", "plate"], ["car_type", "carType"], ["supplier_name", "supplier"]];

export const CarFormDialog = ({ open, onOpenChange, shelfNumber, car, onDone }) => {
  const { t } = useI18n();
  const [form, setForm] = useState({ plate_number: "", car_type: "", supplier_name: "", parts: [] });
  const [busy, setBusy] = useState(false);
  const [invoiceIds, setInvoiceIds] = useState([]);
  const [matches, setMatches] = useState([]);
  const [choices, setChoices] = useState({});

  useEffect(() => {
    if (open) { setInvoiceIds([]); setMatches([]); setChoices({}); }
    if (open) setForm(car ? { plate_number: car.plate_number, car_type: car.car_type, supplier_name: car.supplier_name, parts: car.parts || [] } : { plate_number: "", car_type: "", supplier_name: "", parts: [] });
  }, [open, car]);

  const norm = (s) => (s || "").replace(/[\s\-_/.]/g, "").toLowerCase();

  const submit = async (e) => {
    e.preventDefault();
    const fresh = form.parts.filter((p) => !p.id && p.name.trim() && norm(p.part_number));
    if (fresh.length) {
      try {
        const { data } = await api.get("/warehouse/match", { params: { numbers: fresh.map((p) => p.part_number).join(",") } });
        const found = fresh.flatMap((p) => data.filter((it) => norm(it.part_number) === norm(p.part_number)).map((item) => ({ part: p, item })));
        if (found.length) { setMatches(found); setChoices({}); return; }
      } catch (err) { toast.error(errMsg(err)); return; }
    }
    save();
  };

  const continueSave = async () => {
    setBusy(true);
    for (const [i, m] of matches.entries()) {
      if (choices[i] !== "issue") continue;
      const qty = Math.min(Number(m.part.quantity) || 1, m.item.quantity);
      try { await api.post(`/warehouse/${m.item.id}/issue`, { quantity: qty, plate: form.plate_number }); }
      catch (err) { toast.error(`${m.item.name}: ${errMsg(err)}`); }
    }
    setMatches([]);
    setBusy(false);
    save();
  };

  const save = async () => {
    setBusy(true);
    const payload = { ...form, parts: form.parts.filter((p) => p.name.trim()).map((p) => ({ id: p.id, name: p.name, part_number: p.part_number || null, quantity: Number(p.quantity) })), invoice_attachment_ids: invoiceIds };
    try {
      if (car) await api.put(`/cars/${car.id}`, payload);
      else await api.post("/cars", { ...payload, shelf_number: shelfNumber });
      toast.success(t("success"));
      onOpenChange(false);
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  const rejectInvoices = () => {
    invoiceIds.forEach((id) => api.delete(`/attachments/${id}`).catch(() => {}));
    setInvoiceIds([]);
  };

  const applyInvoice = ({ supplier_name, parts, attachment_id }) => {
    if (attachment_id) setInvoiceIds((ids) => [...ids, attachment_id]);
    setForm((f) => ({
      ...f,
      supplier_name: supplier_name || f.supplier_name,
      parts: [...f.parts.filter((p) => p.delivered || p.name.trim()), ...parts.map((p) => ({ name: p.name, part_number: p.part_number, quantity: p.quantity }))],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="car-form-dialog">
        <DialogHeader><DialogTitle>{car ? t("editCar") : t("addCar")} · {t("shelf")} {shelfNumber}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <InvoiceScanButton onExtracted={applyInvoice} />
          {invoiceIds.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2" data-testid="invoice-saved-note">
              <p className="text-xs font-semibold text-emerald-800">{t("invoiceWillAttach").replace("{n}", invoiceIds.length)}</p>
              <Button type="button" size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 h-7" data-testid="invoice-reject-btn" onClick={rejectInvoices}>{t("dontAttach")}</Button>
            </div>
          )}
          {FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1.5">
              <Label>{t(l)}</Label>
              <Input data-testid={`car-form-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required />
            </div>
          ))}
          <PartsEditor parts={form.parts} onChange={(parts) => setForm({ ...form, parts })} />
          {!car && <p className="text-xs text-slate-500">{t("status")}: <b>{t("received_from_supplier")}</b></p>}
          {matches.length > 0 ? (
            <WarehouseMatchPanel matches={matches} choices={choices} setChoices={setChoices} busy={busy} onCancel={() => setMatches([])} onContinue={continueSave} />
          ) : (
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
            <Button type="submit" disabled={busy} data-testid="car-form-submit">{t("save")}</Button>
          </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
